import { useEntitlements } from "../../context/entitlements"

const remainingText = (included?: number | null, used?: number): string => {
  if (included === -1) return "∞"
  const inc = included ?? 0
  return `${Math.max(0, inc - (used ?? 0))} / ${inc}`
}

/**
 * Card „Abonamentul meu": planul activ + contor pe fiecare beneficiu inclus (cu bară de progres).
 * Se ascunde singur dacă nu există abonament activ. Reutilizat pe Home și pe pagina Credite.
 * Cotele vin din EntitlementsProvider (sursă partajată, cu polling după abonare) — fără fetch propriu.
 */
export const MySubscription = () => {
  const { usage, loading } = useEntitlements()

  if (loading || !usage?.hasActivePlan) return null

  const rows = [
    { label: "Joburi", inc: usage.includedJobPosts, used: usage.usedJobPosts },
    { label: "Promovări", inc: usage.includedJobPromotions, used: usage.usedJobPromotions },
    { label: "Invitații", inc: usage.includedInvitations, used: usage.usedInvitations },
    { label: "Notificări", inc: usage.includedNotifyCampaigns, used: usage.usedNotifyCampaigns },
    { label: "Evenimente", inc: usage.includedEventCreates, used: usage.usedEventCreates },
  ].filter((r) => r.inc === -1 || (r.inc ?? 0) > 0)

  const stipend = usage.monthlyCreditStipend ?? 0
  const resetAt = usage.quotaResetAt ? new Date(usage.quotaResetAt) : null

  return (
    <div className="bg-white rounded-[20px] px-5 py-4 mb-2.5" style={{ border: "1px solid #e5e7eb" }}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-figmaPrimary/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-figmaPrimary">
            Abonament
          </span>
          <p className="text-[15px] font-bold text-figmaBlack">
            Plan {usage.label}{usage.billingInterval === "ANNUAL" ? " (anual)" : ""}
          </p>
        </div>
        {resetAt && (
          <p className="text-[11px] text-figmaDarkGray shrink-0">
            cote noi pe {resetAt.toLocaleDateString("ro-RO", { day: "2-digit", month: "short" })}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2.5">
        {rows.map((r) => {
          const unlimited = r.inc === -1
          const inc = r.inc ?? 0
          const remaining = Math.max(0, inc - (r.used ?? 0))
          const pct = unlimited ? 100 : inc > 0 ? (remaining / inc) * 100 : 0
          return (
            <div key={r.label}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[12px] text-figmaDarkGray">{r.label}</span>
                {unlimited ? (
                  <span className="text-[17px] font-bold text-figmaBlack leading-none">∞</span>
                ) : (
                  <span className="text-[12px] font-bold text-figmaBlack">{remainingText(r.inc, r.used)}</span>
                )}
              </div>
              <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${pct}%`, background: unlimited ? "#486af6" : pct <= 20 ? "#f59e0b" : "#486af6" }}
                />
              </div>
            </div>
          )
        })}
      </div>

      {stipend > 0 && (
        <p className="text-[11px] text-figmaDarkGray mt-3">
          Include un <span className="font-bold text-figmaBlack">pachet lunar de {stipend} credite</span>, adăugate automat în sold.
        </p>
      )}
    </div>
  )
}
